import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNewArrivals, setPage } from '../store/slices/productSlice';
import Loader from '../components/common/loader';

const NewArrivals = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { newArrivals, pagination, loading, error } = useSelector(state => state.products);

  const [sortOption, setSortOption] = useState('createdAt_desc');

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const page = parseInt(queryParams.get('page')) || 1;
    const sort = queryParams.get('sort') || 'createdAt_desc';
    setSortOption(sort);

    dispatch(setPage(page));

    const [sortBy, sortOrder] = sort.split('_');

    dispatch(fetchNewArrivals({
      page,
      limit: 12,
      sortBy,
      sortOrder
    }));
  }, [dispatch, location.search]);

  const handleSortChange = (e) => {
    const newSortOption = e.target.value;
    setSortOption(newSortOption);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('sort', newSortOption);
    queryParams.set('page', 1);
    navigate(`/new-arrivals?${queryParams.toString()}`);
  };

  const handlePageChange = (newPage) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('page', newPage);
    navigate(`/new-arrivals?${queryParams.toString()}`);
  };

  const calculateDiscount = (mrp, price) => {
    if (!mrp || !price || mrp <= price) return 0;
    return Math.round(((mrp - price) / mrp) * 100);
  };

  // Safely extract error message — error may be an object or string
  const errorMessage = error.newArrivals
    ? (typeof error.newArrivals === 'string' ? error.newArrivals : error.newArrivals?.message || 'Something went wrong')
    : null;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold mb-2">New Arrivals</h1>
          <p className="text-gray-600">Discover our latest products</p>
        </div>

        <div className="mt-4 sm:mt-0">
          <select
            value={sortOption}
            onChange={handleSortChange}
            className="border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="createdAt_desc">Newest First</option>
            <option value="sellingPrice_asc">Price: Low to High</option>
            <option value="sellingPrice_desc">Price: High to Low</option>
          </select>
        </div>
      </div>

      {loading.newArrivals ? (
        <div className="flex justify-center items-center h-64">
          <Loader size="lg" />
        </div>
      ) : errorMessage ? (
        <div className="bg-red-100 text-red-700 p-4 rounded">
          {errorMessage}
        </div>
      ) : !newArrivals || newArrivals.length === 0 ? (
        <div className="bg-yellow-100 text-yellow-800 p-4 rounded">
          No new arrivals found.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {newArrivals.map(product => (
              <div key={product.productId} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition">
                <div className="relative">
                  <Link to={`/product/${product.productId}`}>
                    <div className="h-48 overflow-hidden">
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </Link>
                  <div className="absolute top-2 right-2">
                    <span className="bg-pink-500 text-white text-xs px-2 py-1 rounded">New</span>
                  </div>
                </div>

                <div className="p-4">
                  <Link to={`/product/${product.productId}`}>
                    <h3 className="font-medium text-gray-900 mb-1 truncate hover:text-blue-600">
                      {product.name}
                    </h3>

                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-500">{product.category}</span>
                    </div>

                    <div className="flex items-end mb-3">
                      <span className="text-lg font-bold text-gray-900">
                        ₹{product.sellingPrice || product.price}
                      </span>
                      {product.mrp > (product.sellingPrice || product.price) && (
                        <>
                          <span className="text-sm text-gray-500 line-through ml-2">
                            ₹{product.mrp}
                          </span>
                          <span className="text-sm text-green-600 ml-2">
                            {calculateDiscount(product.mrp, product.sellingPrice || product.price)}% off
                          </span>
                        </>
                      )}
                    </div>
                  </Link>

                  <Link
                    to={`/product/${product.productId}`}
                    className="block w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition text-center text-sm"
                  >
                    View Product
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex justify-center mt-8">
              <div className="flex space-x-1">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className={`px-3 py-1 rounded text-sm ${
                    pagination.page === 1
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Previous
                </button>

                {[...Array(pagination.pages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handlePageChange(i + 1)}
                    className={`px-3 py-1 rounded text-sm ${
                      pagination.page === i + 1
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className={`px-3 py-1 rounded text-sm ${
                    pagination.page === pagination.pages
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NewArrivals;